import i18n from '~/components/i18n'
import { StepId } from './constants'
import dayjs from 'dayjs'
import { getDateWithoutTime } from '~/utils/helpers'

export function getStepHeadMessage(stepId: StepId): string {
  switch (stepId) {
    case StepId.CROP:
      return i18n.t('Enter information for the crop and field you are assessing.')
    case StepId.SOIL:
      return i18n.t('Specify the soil characteristics of the field in your assessment. Nb. CFT is currently only suitable for assessments on mineral soils with a Soil Organic Carbon content of less than 12%.')
    case StepId.INPUTS:
      return i18n.t(`Data entered here affect the greenhouse gas emissions from "fertiliser production", "crop protection products" and "nitrous oxide (N<sub>2</sub>0) emissions from soil". 
          Enter data on fertiliser inputs and crop protection applications before, during and after your crop growing period. Include any inputs to any catch or cover crops associated with your primary crop.
          Don't forget to enter the fuel used to apply these inputs in the 'Fuel & Energy' section.The tool calculates emissions from production and application of fertilisers and crop protection products (scope 3), 
          except for manure, where production emissions are allocated to the dairy or meat footprints in the tool and not to the crop footprint. In the results, pesticides emissions are summed up, while fertiliser production emissions are shown separately and consider different regions of production.`)
    case StepId.FUEL_ENERGY:
      return i18n.t(`Enter data for fuel and electricity. Usage data from meters and fuel records is most accurate, and should be entered in Direct energy use. 
        If you do not have fuel data records for field operations, estimates of fuel use can be calculated in Field operations energy use. Include all fuel used for applying inputs.
         Please refer only to the energy used to manufacture the product or grow the plant.`)
    case StepId.IRRIGATION:
      return i18n.t('If calculating GHG AND irrigation energy is not captured in section Direct energy use, use this estimator for irrigation energy. If calculating water metrics, use this to enter water quantity. You may add multiple entries with different methods of irrigation and sources of water.')
    case StepId.CARBON:
      return i18n.t('This page deals with carbon stored by or released from your soil and above ground biomass due to recent changes in management practices. These include land-use change, tillage changes or changing cover crop practices. They can affect net carbon capture or release, impacting emissions. They can also drive cumulative carbon stock. (The effects can be seen in the results section under \'carbon stock changes\')')
    case StepId.TRANSPORT:
      return i18n.t('Inbound transportation of inputs - such as manures from a neighbour, or fertiliser from the merchant - to your farm should be included. For each transportation mode, calculate or estimate the weight of goods and distances transported.')
    case StepId.RESULTS:
      return 'Include some message here...'
    default:
      return ''
  }
}

export function preparePayload(data) {
  Object.keys(data).forEach(key => {
    if(key === 'fertilizers') {
      data[key]?.forEach(fertilizer => {
        const date = getDateWithoutTime(fertilizer?.applicationDate)
        if(dayjs(date).isValid) {
          fertilizer.applicationDate = dayjs(date).format('YYYY-MM-DD')
        }
      })
    }
    if (typeof (data[key]) === 'string') {
      data[key] = data[key].replace(',', '.')
    }
    else if(data[key]) {
      Object.entries(data[key])?.forEach(([propKey, value]) => {
        if (typeof value === 'string') {
          data[key][propKey] = value.replace(',', '.');
          return
        }

        if (typeof value === 'object' && value !== null) {
          Object.entries(value)?.forEach(([itemKey, itemValue]) => {
            if (typeof itemValue === 'string') {
              data[key][propKey][itemKey] = itemValue.replace(',', '.');
            }
          });
          return
        }

        if (Array.isArray(value)) {
          value?.forEach((item, index) => {
            if (typeof item === 'object' && item !== null) {
              Object.entries(item)?.forEach(([itemKey, itemValue]) => {
                if (typeof itemValue === 'string') {
                  data[key][propKey][index][itemKey] = itemValue.replace(',', '.');
                }
              });
            }
          });
        }
      })
    }
  })
  return data
}

export const calculateNitrogenValidation = (customFertilizer) => {
  const nTotal = parseFloat(customFertilizer?.nTotal) || 0
  const nAmmonia = parseFloat(customFertilizer?.nAmmonia) || 0
  const nNitric = parseFloat(customFertilizer?.nNitric) || 0
  const nUrea = parseFloat(customFertilizer?.nUrea) || 0
  const sum = nAmmonia + nNitric + nUrea
  return Math.abs(nTotal - sum) < 0.01
}
